import { UserServiceAccountWithSecret } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { TitleText } from '@/components/TitleText';
import { BodyText } from '@/components/BodyText';
import { useCreateServiceAccount } from '@/hooks/api/service-accounts/useCreateServiceAccount';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { getApiDocumentationUrl } from '@/services/api';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState, useEffect } from 'react';

const StyledServiceAccountDialog = styled(Dialog)({
  '& .MuiPaper-root': {
    borderRadius: '12px',
  },
});

const StyledDialogTitle = styled(Box)({
  padding: '24px 8px 0 24px',
});

const StyledDialogContent = styled(Box)({
  padding: '8px 24px 24px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const StyledDialogActions = styled(DialogActions)({
  padding: '0 24px 24px 24px',
});

const StyledActionButton = styled(Button)({
  textTransform: 'none',
  borderRadius: '12px',
  padding: '8px 16px',
});

const CredentialLabel = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  display: 'block',
}));

const StyledCredentialTextField = styled(TextField)({
  '& .MuiInputBase-input': {
    fontFamily: 'monospace',
    fontSize: '14px',
  },
});

enum ModalStep {
  FORM = 'form',
  SECRET_DISPLAY = 'secret-display',
}

export const CreateServiceAccountModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { createServiceAccount, isCreating, error, mutationData } = useCreateServiceAccount();
  const { copy } = useCopyToClipboard();

  const [step, setStep] = useState<ModalStep>(ModalStep.FORM);
  const [name, setName] = useState('');
  const [createdAccount, setCreatedAccount] = useState<UserServiceAccountWithSecret | undefined>();
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (mutationData?.data.serviceAccount) {
      setCreatedAccount(mutationData.data.serviceAccount);
      setStep(ModalStep.SECRET_DISPLAY);
    }
  }, [mutationData]);

  const handleClose = () => {
    setName('');
    setStep(ModalStep.FORM);
    setCreatedAccount(undefined);
    setShowSecret(false);
    onClose();
  };

  const handleCreate = () => {
    createServiceAccount({ name });
  };

  const handleAcknowledge = () => {
    handleClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && name.trim() && !isCreating) {
      event.preventDefault();
      handleCreate();
    }
  };

  return (
    <StyledServiceAccountDialog
      open={isOpen}
      onClose={step === ModalStep.SECRET_DISPLAY ? undefined : handleClose}
      fullWidth
      maxWidth='sm'
    >
      {step === ModalStep.FORM && (
        <>
          <StyledDialogTitle>
            <TitleText>Create Service Account</TitleText>
          </StyledDialogTitle>
          <StyledDialogContent>
            <ApiErrorAlert error={error} />
            <TextField
              label='Name'
              value={name}
              onChange={event => setName(event.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
              required
              helperText='Human-readable identifier for this service account'
              autoFocus
            />
          </StyledDialogContent>

          <StyledDialogActions>
            <StyledActionButton onClick={handleClose} disabled={isCreating}>
              Cancel
            </StyledActionButton>
            <StyledActionButton
              onClick={handleCreate}
              variant='contained'
              color='primary'
              disabled={isCreating || !name.trim()}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </StyledActionButton>
          </StyledDialogActions>
        </>
      )}

      {step === ModalStep.SECRET_DISPLAY && createdAccount && (
        <>
          <StyledDialogTitle>
            <TitleText>Save Your Credentials</TitleText>
          </StyledDialogTitle>
          <StyledDialogContent sx={{ gap: '16px' }}>
            <Alert severity='warning'>
              <BodyText>
                The client secret will only be shown once. <strong>Copy it now!</strong>
              </BodyText>
            </Alert>

            <Box>
              <CredentialLabel>
                <BodyText variant='label'>Client ID</BodyText>
              </CredentialLabel>
              <StyledCredentialTextField
                fullWidth
                value={createdAccount.clientId}
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position='end'>
                        <Tooltip title='Copy Client ID'>
                          <IconButton
                            onClick={() => void copy(createdAccount.clientId, 'Client ID')}
                            edge='end'
                            aria-label='Copy Client ID'
                          >
                            <ContentCopy fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            <Box>
              <CredentialLabel>
                <BodyText variant='label'>Client Secret</BodyText>
              </CredentialLabel>
              <StyledCredentialTextField
                fullWidth
                type={showSecret ? 'text' : 'password'}
                value={createdAccount.secret}
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position='end' sx={{ gap: '8px' }}>
                        <Tooltip title={showSecret ? 'Hide Client Secret' : 'Show Client Secret'}>
                          <IconButton
                            onClick={() => setShowSecret(!showSecret)}
                            edge='end'
                            aria-label={showSecret ? 'Hide Client Secret' : 'Show Client Secret'}
                          >
                            {showSecret ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Copy Client Secret'>
                          <IconButton
                            onClick={() => void copy(createdAccount.secret, 'Client Secret')}
                            edge='end'
                            aria-label='Copy Client Secret'
                          >
                            <ContentCopy fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            <Alert severity='info'>
              <Box>
                <BodyText sx={{ display: 'block', mb: 1 }}>
                  <strong>How to use these credentials:</strong>
                </BodyText>
                <BodyText>
                  Use the OAuth2 client credentials flow to obtain an access token. See the{' '}
                  <Link href={getApiDocumentationUrl()} target='_blank' rel='noopener noreferrer'>
                    API documentation
                  </Link>{' '}
                  for details.
                </BodyText>
              </Box>
            </Alert>
          </StyledDialogContent>

          <StyledDialogActions>
            <StyledActionButton onClick={handleAcknowledge} variant='contained' color='primary' fullWidth>
              I&apos;ve Saved These
            </StyledActionButton>
          </StyledDialogActions>
        </>
      )}
    </StyledServiceAccountDialog>
  );
};
